import React from 'react';

// --- No se necesitan definiciones de iconos SVG locales, usaremos URLs de imagen ---

// Datos por defecto, ahora apuntando a URLs de imagen de ejemplo (placeholders)
const defaultItems = [
  {
    title: "Archivos Vectoriales",
    description: "Archivos geoespaciales para la consulta SIG.",
    imageUrl: "https://placehold.co/64x64/0284c7/ffffff?text=MAP", // sky-600
    baseColor: "sky",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/PaisajeAgavero/Descargas/VectorialesPaisajeAgavero.zip",
    target: "_blank",
  },
  {
    title: "Fichas UGAs",
    description: "Informacion detallada de cada Unidad de Gestión Ambiental (UGA).",
    imageUrl: "https://placehold.co/64x64/059669/ffffff?text=LIST", // green-600
    baseColor: "green",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/PaisajeAgavero/Descargas/Fichas%20de%20las%20Unidades%20de%20Gesti%C3%B3n%20Ambiental%20Regi%C3%B3n%20Paisaje%20Agavero.pdf",
    target: "_blank",
  },
  {
    title: "Criterios",
    description: "Regulaciones ecológicas para la preservación y restauración del territorio.",
    imageUrl: "https://placehold.co/64x64/ea580c/ffffff?text=FILE", // orange-600
    baseColor: "orange",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/PaisajeAgavero/Descargas/Criterios%20Regi%C3%B3n%20Paisaje%20Agavero.pdf",
    target: "_blank",
  },
  {
    title: "Estrategias Ambientales",
    description: "Proyectos de orientación para la protección del entorno natural.",
    imageUrl: "https://placehold.co/64x64/dc2626/ffffff?text=TARGET", // red-600
    baseColor: "red",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/PaisajeAgavero/Descargas/Estrategias%20Regi%C3%B3n%20Paisaje%20Agavero.pdf",
    target: "_blank",
  },
  {
    title: "Lineamientos",
    description: "Conservar, proteger y restaurar los recursos naturales de la región.",
    imageUrl: "https://placehold.co/64x64/4f46e5/ffffff?text=LIGHT", // indigo-600
    baseColor: "indigo",
    link: "https://siga.jalisco.gob.mx/ordenamiento/archivos/PaisajeAgavero/Descargas/Lineamientos%20ecol%C3%B3gicos%20Regi%C3%B3n%20Paisaje%20Agavero.pdf",
    target: "_blank",
  },
  {
    title: "Mapa Interactivo",
    description: "Visualiza toda la información SIG directamente en el navegador.",
    imageUrl: "https://placehold.co/64x64/0d9488/ffffff?text=COMPASS", // teal-600
    baseColor: "teal",
    link: "https://siga.jalisco.gob.mx/ordenamiento/mapa/index.html?capa=paisaje_agavero",
    target: "_blank",
  },
];

/**
 * Componente de tarjeta interactiva.
 * Renderiza una imagen si se proporciona 'imageUrl', si no, intenta renderizar 'Icon'.
 * @param {object} props - Propiedades del componente.
 * @param {string} props.title - Título de la tarjeta.
 * @param {string} props.description - Descripción del contenido.
 * @param {string} props.imageUrl - URL de la imagen a usar.
 * @param {React.Component} [props.Icon] - Icono componente React (fallback).
 * @param {string} props.baseColor - Color base para el borde y texto (ej: "sky", "green").
 * @param {string} props.link - URL de descarga o recurso.
 * @param {string} props.target - Target del enlace (ej: "_blank").
 */
const DownloadCard = ({ title, description, imageUrl, Icon, baseColor, link, target }) => (
  <a
    href={link}
    target={target}
    rel={target === '_blank' ? 'noopener noreferrer' : undefined}
    // Aplica la estructura solicitada: fondo blanco, sombra 2xl, border-b-4 con color dinámico y animación de elevación
    className={`
      group flex flex-col items-center p-10 rounded-3xl shadow-2xl
      bg-white dark:bg-gray-800
      border-b-4 border-${baseColor}-400 hover:border-${baseColor}-600
      transition duration-300 transform hover:-translate-y-1 cursor-pointer
      dark:shadow-gray-900/50
    `}
  >
    {/* Contenedor del Icono/Imagen */}
    <div className="w-16 h-16 mb-4 flex items-center justify-center">
      {imageUrl ? (
        // Renderiza una imagen
        <img 
          src={imageUrl} 
          alt={`${title} icon`} 
          className="w-full h-full object-contain" 
          onError={(e) => { e.target.onerror = null; e.target.src="https://placehold.co/64x64/888888/ffffff?text=IMG" }} // Fallback simple
        />
      ) : Icon ? (
        // Fallback a Icon Component (si se hubiera definido)
        // Se usa un Fragmento de React (<></>) para envolver los elementos hermanos y corregir el error de compilación.
        <>
          {/* Nota: Se usa un truco para que Tailwind genere las clases dinámicas correctamente */}
          <span className="hidden text-sky-600 group-hover:text-sky-800 text-green-600 group-hover:text-green-800 text-orange-600 group-hover:text-orange-800 text-red-600 group-hover:text-red-800 text-indigo-600 group-hover:text-indigo-800 text-teal-600 group-hover:text-teal-800 dark:group-hover:text-sky-400 dark:group-hover:text-green-400 dark:group-hover:text-orange-400 dark:group-hover:text-red-400 dark:group-hover:text-indigo-400 dark:group-hover:text-teal-400"></span>
          <span className="hidden group-hover:text-sky-700 group-hover:text-green-700 group-hover:text-orange-700 group-hover:text-red-700 group-hover:text-indigo-700 group-hover:text-teal-700"></span>
          <Icon className={`w-16 h-16 text-${baseColor}-600 transition group-hover:text-${baseColor}-800 dark:group-hover:text-${baseColor}-400`} />
        </>
      ) : (
        // Fallback si no hay imagen ni icono
        <div className="w-16 h-16 bg-gray-300 rounded-full flex items-center justify-center text-xs text-gray-700">?</div>
      )}
    </div>

    {/* Título - usa el estilo font-extrabold solicitado */}
    <h3 className={`text-2xl font-extrabold text-gray-900 dark:text-white group-hover:text-${baseColor}-700 dark:group-hover:text-${baseColor}-400 transition`}>
      {title}
    </h3>

    {/* Descripción */}
    <p className="text-base mt-3 text-center text-gray-500 dark:text-gray-400">
      {description}
    </p>
    
    {/* Etiqueta de acción (similar al ejemplo, pero más discreto) */}
    <div className={`mt-4 text-sm font-semibold text-${baseColor}-600 dark:text-${baseColor}-400 transition-colors`}>
      {title.includes("Vectoriales") ? "Descargar .ZIP" : "Ver Documento"}
    </div>
  </a>
);


/**
 * Componente principal y portable para la sección de descargas.
 * Acepta un prop 'dataItems' para hacer los elementos configurables.
 * @param {object} props - Propiedades del componente.
 * @param {Array<object>} props.dataItems - Lista de elementos a mostrar.
 */
const DownloadSection = ({ dataItems = defaultItems }) => {
  const itemsToRender = dataItems || defaultItems;

  return (
    // Se mantiene el fondo gris sutil
    <div className="min-h-screen bg-gray-100 dark:bg-gray-900 py-16 px-4 sm:px-6 lg:px-8 font-sans transition-colors duration-500">
      <div className="max-w-7xl mx-auto">
        {/* Título de la Sección */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 dark:text-white leading-tight mb-4">
            Descargas
          </h2>
          <p className="mt-4 text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto">
            Accede directamente a la documentación clave para la preservación y gestión.
          </p>
        </div>

        {/* Grid de Tarjetas */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {itemsToRender.map((item, index) => (
            <DownloadCard
              key={index}
              title={item.title}
              description={item.description}
              imageUrl={item.imageUrl} // Pasamos la nueva prop
              Icon={item.Icon}        // Mantenemos Icon como fallback opcional
              baseColor={item.baseColor}
              link={item.link}
              target={item.target}
            />
          ))}
        </div>
      </div>
      
      {/* Script de Tailwind CSS (se asume cargado en el entorno React) */}
      <script src="https://cdn.tailwindcss.com"></script>
    </div>
  );
};

export default DownloadSection;